import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo, ErrorBox } from "@/components/common";
import { LOGIN, REGISTER } from "@/constants/testIds";

const inp = "w-full border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:border-red-600 bg-white";
const btn = "w-full py-2.5 bg-red-600 text-white font-semibold hover:bg-red-700 disabled:opacity-50";

const Shell = ({ title, sub, children }) => (
  <div className="min-h-screen grid lg:grid-cols-2">
    <div className="hidden lg:flex bg-gray-900 grid-bg text-white p-12 flex-col justify-between">
      <Link to="/"><Logo dark /></Link>
      <div><h2 className="text-4xl font-black leading-tight">Your AI IT Administrator.</h2><p className="text-gray-400 mt-4 max-w-md">Monitoring, diagnosing and resolving IT problems 24/7 — under your policies.</p></div>
      <p className="text-xs text-gray-500">Protected by MFA, tenant isolation and a tamper-evident audit log.</p>
    </div>
    <div className="flex items-center justify-center p-6">
      <div className="w-full max-w-sm fade-up">
        <Link to="/" className="lg:hidden block mb-8"><Logo /></Link>
        <h1 className="text-2xl font-black">{title}</h1>
        {sub && <p className="text-sm text-gray-500 mt-1">{sub}</p>}
        <div className="mt-8">{children}</div>
      </div>
    </div>
  </div>
);

const afterLogin = (data, nav) => {
  if (data.mfa_setup_required) return nav("/mfa-setup");
  if (data.user.platform_role === "super_admin" && !(data.memberships || []).length) return nav("/admin/master");
  nav("/portal");
};

export function LoginPage() {
  const { apply } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ email: "", password: "" });
  const [mfa, setMfa] = useState(null);
  const [code, setCode] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      if (mfa) {
        const r = await api.post("/auth/mfa/login", { mfa_token: mfa, code });
        apply(r.data);
        afterLogin(r.data, nav);
      } else {
        const r = await api.post("/auth/login", { ...f, email: f.email.trim() });
        if (r.data.mfa_required) setMfa(r.data.mfa_token);
        else {
          apply(r.data);
          afterLogin(r.data, nav);
        }
      }
    } catch (e2) {
      setErr(formatErr(e2));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Shell title={mfa ? "Two-factor authentication" : "Sign in to CodeRed"} sub={mfa ? "Enter the 6-digit code from your authenticator app." : null}>
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <ErrorBox error={err} />
        {mfa ? (
          <input autoFocus inputMode="numeric" maxLength={8} placeholder="123456" className={`${inp} font-mono tracking-[0.4em] text-center text-lg`} value={code} onChange={(e) => setCode(e.target.value.trim())} data-testid="login-mfa-code-input" />
        ) : (
          <>
            <input required type="email" placeholder="Email" className={inp} value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid={LOGIN.emailInput} />
            <input required type="password" placeholder="Password" className={inp} value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid={LOGIN.passwordInput} />
          </>
        )}
        <button disabled={busy} className={btn} data-testid={mfa ? "login-mfa-submit-button" : LOGIN.submitButton}>{busy ? "Signing in…" : mfa ? "Verify" : "Sign in"}</button>
      </form>
      {!mfa && (
        <div className="mt-6 flex justify-between text-sm">
          <Link to="/forgot-password" className="text-gray-500 hover:text-gray-900" data-testid={LOGIN.forgotPasswordLink}>Forgot password?</Link>
          <Link to="/register" className="text-red-600 font-semibold" data-testid={LOGIN.registerLink}>Create account</Link>
        </div>
      )}
    </Shell>
  );
}

export function RegisterPage() {
  const { apply } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ name: "", email: "", password: "", confirm: "", org_name: "", org_type: "business" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (f.password !== f.confirm) return setErr("Passwords do not match");
    setBusy(true);
    setErr("");
    try {
      const { confirm, ...body } = f;
      const r = await api.post("/auth/register", body);
      apply(r.data);
      toast.success("Organization created. Check your inbox to verify your email.");
      nav("/mfa-setup");
    } catch (e2) {
      setErr(formatErr(e2));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Shell title="Start your 15-day trial" sub="Up to 10 devices · AI diagnostics included · no card required · trial endpoint-days billed on first statement">
      <form onSubmit={submit} className="space-y-3" data-testid="register-form">
        <ErrorBox error={err} />
        <input required placeholder="Organization name" className={inp} value={f.org_name} onChange={(e) => setF({ ...f, org_name: e.target.value })} data-testid="register-org-input" />
        <select className={inp} value={f.org_type} onChange={(e) => setF({ ...f, org_type: e.target.value })} data-testid="register-org-type-select">
          <option value="business">Business / IT department</option><option value="msp">Managed service provider (MSP)</option>
        </select>
        <input required placeholder="Your name" className={inp} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid={REGISTER.nameInput} />
        <input required type="email" placeholder="Work email" className={inp} value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid={REGISTER.emailInput} />
        <input required minLength={10} type="password" placeholder="Password (10+ chars, letters & numbers)" className={inp} value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid={REGISTER.passwordInput} />
        <input required type="password" placeholder="Confirm password" className={inp} value={f.confirm} onChange={(e) => setF({ ...f, confirm: e.target.value })} data-testid={REGISTER.passwordConfirmInput} />
        <button disabled={busy} className={btn} data-testid={REGISTER.submitButton}>{busy ? "Creating…" : "Create organization"}</button>
        <p className="text-xs text-gray-500">By continuing you agree to the <Link to="/legal/terms" className="underline">Terms</Link> and <Link to="/legal/privacy" className="underline">Privacy Policy</Link>.</p>
      </form>
      <p className="mt-6 text-sm">Already have an account? <Link to="/login" className="text-red-600 font-semibold" data-testid={REGISTER.loginLink}>Sign in</Link></p>
    </Shell>
  );
}

export function VerifyEmailPage() {
  const [sp] = useSearchParams();
  const [state, setState] = useState("checking");
  const { refresh } = useAuth();
  useEffect(() => {
    api.post("/auth/verify-email", { token: sp.get("token") || "" })
      .then(() => { setState("ok"); refresh(); })
      .catch((e) => setState(formatErr(e)));
  }, [sp, refresh]);
  return (
    <Shell title="Email verification">
      <div data-testid="verify-email-status">{state === "checking" ? "Verifying…" : state === "ok" ? "Your email is verified. You can now enroll devices." : <ErrorBox error={state} />}</div>
      <Link to="/portal" className={`${btn} block text-center mt-6`} data-testid="verify-continue-btn">Continue to portal</Link>
    </Shell>
  );
}

export function ForgotPage() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    await api.post("/auth/forgot-password", { email }).catch(() => {});
    setSent(true);
  };
  return (
    <Shell title="Reset your password">
      {sent ? <p data-testid="forgot-sent-msg" className="text-sm">If an account exists for that email, a reset link has been sent. It expires in 1 hour.</p> : (
        <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
          <input required type="email" placeholder="Email" className={inp} value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email-input" />
          <button className={btn} data-testid="forgot-submit-btn">Send reset link</button>
        </form>
      )}
      <Link to="/login" className="block mt-6 text-sm text-gray-500">Back to sign in</Link>
    </Shell>
  );
}

export function ResetPage() {
  const [sp] = useSearchParams();
  const invite = sp.get("invite") === "1";
  const nav = useNavigate();
  const [f, setF] = useState({ password: "", name: "" });
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/auth/reset-password", { token: sp.get("token") || "", password: f.password, name: f.name || null });
      toast.success(invite ? "Account activated. Please sign in." : "Password updated. Please sign in.");
      nav("/login");
    } catch (e2) {
      setErr(formatErr(e2));
    }
  };
  return (
    <Shell title={invite ? "Accept your invitation" : "Choose a new password"}>
      <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
        <ErrorBox error={err} />
        {invite && <input placeholder="Your name" className={inp} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="reset-name-input" />}
        <input required minLength={10} type="password" placeholder="New password" className={inp} value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="reset-password-input" />
        <button className={btn} data-testid="reset-submit-btn">{invite ? "Activate account" : "Update password"}</button>
      </form>
    </Shell>
  );
}

export function MfaSetupPage() {
  const { session, refresh } = useAuth();
  const nav = useNavigate();
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [err, setErr] = useState("");
  useEffect(() => {
    if (session && !session.user.mfa_enabled) api.post("/auth/mfa/setup").then((r) => setSetup(r.data)).catch((e) => setErr(formatErr(e)));
  }, [session]);
  if (session?.user?.mfa_enabled) {
    return <Shell title="MFA is enabled"><Link to="/portal" className={`${btn} block text-center`}>Continue</Link></Shell>;
  }
  const enable = async (e) => {
    e.preventDefault();
    try {
      await api.post("/auth/mfa/enable", { code });
      toast.success("Two-factor authentication enabled");
      await refresh();
      nav(session.user.platform_role === "super_admin" && !session.memberships.length ? "/admin/master" : "/portal");
    } catch (e2) {
      setErr(formatErr(e2));
    }
  };
  return (
    <Shell title="Secure your account" sub="Administrators must use an authenticator app (Google Authenticator, Microsoft Authenticator, 1Password…).">
      <ErrorBox error={err} />
      {setup && (
        <form onSubmit={enable} className="space-y-4" data-testid="mfa-setup-form">
          <img src={setup.qr_data_url} alt="MFA QR code" className="w-44 h-44 border border-gray-200" data-testid="mfa-qr-image" />
          <div className="text-xs text-gray-500">Or enter this key manually:<div className="font-mono text-gray-900 break-all mt-1" data-testid="mfa-secret-text">{setup.secret}</div></div>
          <input required inputMode="numeric" placeholder="6-digit code" className={`${inp} font-mono tracking-[0.3em]`} value={code} onChange={(e) => setCode(e.target.value.trim())} data-testid="mfa-code-input" />
          <button className={btn} data-testid="mfa-enable-btn">Enable MFA</button>
        </form>
      )}
      <Link to="/portal" className="block mt-6 text-xs text-gray-500" data-testid="mfa-skip-link">Skip for now (administrative actions will stay locked)</Link>
    </Shell>
  );
}
